/**
 * 비밀번호 재설정 모달 (Phase 4-1) — admin 이 다른 사용자 비번 강제 변경.
 *
 * 본인 비번 변경은 마이페이지에서 (백엔드도 본인 차단).
 * 변경 후 사용자에게 새 비번을 별도 채널로 안전하게 전달해야 함.
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as adminApi from '../lib/admin-api';
import { useDirtyClose } from '../lib/useDirtyClose';

interface Props {
  userId: string;
  userName: string | null;
  onClose: () => void;
  onDone: () => void;
}

export function ResetPasswordModal({ userId, userName, onClose, onDone }: Props) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const { markDirty, requestClose } = useDirtyClose(onClose);

  const mut = useMutation({
    mutationFn: () => adminApi.resetUserPassword(userId, pw1),
    onSuccess: () => {
      setFeedback('비밀번호가 재설정되었습니다. 사용자에게 안전하게 전달하세요.');
      // 1.5초 후 자동 닫기
      setTimeout(() => onDone(), 1500);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    if (pw1.length < 8) {
      setFeedback('비밀번호는 최소 8자 이상이어야 합니다.');
      return;
    }
    if (pw1 !== pw2) {
      setFeedback('비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    mut.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10"
    >
      <div
        className="bg-white rounded shadow-xl w-[480px] max-w-[95vw]"
        onClick={(e) => e.stopPropagation()}
        onInput={markDirty}
        onChange={markDirty}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="ui-modal-title">비밀번호 재설정</h2>
          <button
            type="button"
            onClick={requestClose}
            className="ui-close"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900">
            <strong>{userName ?? userId}</strong>{' '}
            <span className="text-amber-700">({userId})</span> 계정의 비밀번호를
            새로 설정합니다. 변경 후 안전한 채널로 사용자에게 전달하세요.
          </div>
          <div>
            <label className="ui-label">새 비밀번호 (최소 8자)</label>
            <input
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              required
              minLength={8}
              autoFocus
              className="ui-input w-full mt-1"
            />
          </div>
          <div>
            <label className="ui-label">비밀번호 확인</label>
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              className="ui-input w-full mt-1"
            />
          </div>

          {(feedback || mut.error) && (
            <div
              className={`text-xs ${
                feedback?.includes('재설정되었습니다')
                  ? 'text-emerald-700'
                  : 'text-red-600'
              }`}
            >
              {feedback || (mut.error as Error)?.message}
            </div>
          )}

          <div className="flex justify-end gap-2 -mx-6 -mb-6 px-6 py-4 border-t border-slate-200 bg-slate-50">
            <button
              type="button"
              onClick={requestClose}
              className="btn btn-secondary"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="btn btn-primary"
            >
              {mut.isPending ? '재설정 중…' : '재설정'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
